// =============================================================
// EditModeContainer.jsx (React class)
// Contenedor genérico para renderizar dinámicamente distintos componentes
// en función de un "modo" recibido desde el padre y, opcionalmente,
// permitir alternar internamente entre varias vistas dentro de un modo.
// -------------------------------------------------------------
// CARACTERÍSTICAS
// - Registro de componentes por clave: { A: CompA, B: CompB, C: CompC }
// - Variantes (modos) definidas por el padre: cada variante establece
//   qué componente se muestra por defecto y a cuáles se puede alternar.
// - Soporta control total desde el padre (controlled) o semiautónomo
//   (defaultValue / defaultViewKey) con callbacks onChange/onViewChange.
// - UI opcional para cambiar de vista (botones simples con MDB).
// - Pasa props comunes (passProps) y props específicas por componente.
// -------------------------------------------------------------
// EJEMPLO 1 (público vs admin):
// <EditModeContainer
//   value={isLoggedIn ? 'admin' : 'public'}
//   registry={{ CarouselSection, CarouselAdmin }}
//   variants={[
//     { name: 'public',  defaultKey: 'CarouselSection', allowed: ['CarouselSection'] },
//     { name: 'admin',   defaultKey: 'CarouselAdmin',   allowed: ['CarouselAdmin'] },
//   ]}
//   passProps={{ project }}
// />
//
// EJEMPLO 2 (A/B/C, con el modo C que permite alternar):
// <EditModeContainer
//   value={mode} // 'A' | 'B' | 'C'
//   onChange={setMode}
//   registry={{ A: CompA, B: CompB, C: CompC }}
//   variants=[
//     { name: 'A', defaultKey: 'A', allowed: ['A'] },
//     { name: 'B', defaultKey: 'B', allowed: ['B'] },
//     { name: 'C', defaultKey: 'A', allowed: ['A','B','C'], labels: { A: 'Ver A', B: 'Ir a B', C: 'Ir a C' } },
//   ]
//   showControls='auto' // muestra controles sólo si hay más de una vista permitida
// />
// =============================================================

import React from 'react';
import { MDBBtn, MDBBtnGroup } from 'mdb-react-ui-kit';

import "./EditModeContainer.css";

function isFunction(fn){ return typeof fn === 'function'; }

export default class EditModeContainer extends React.Component {
  static defaultProps = {
    // Clave del modo seleccionado (controlled). Si no se pasa, usa defaultValue
    value: undefined,
    defaultValue: undefined,

    // Clave de la vista (componente) activa dentro del modo (controlled)
    viewKey: undefined,
    defaultViewKey: undefined,

    // { key: ReactComponent }
    registry: {},

    // [{ name, defaultKey, allowed?: string[], labels?: { [key]:label } }]
    variants: [],

    // Props comunes que se pasarán a TODO componente
    passProps: {},
    // Props específicos por componente: { key: { ... } }
    componentProps: {},

    // Callbacks
    onChange: undefined,     // (modeName) => void
    onViewChange: undefined, // (viewKey) => void

    // Controles de UI
    showControls: 'auto', // true | false | 'auto'
    controlsPosition: 'top', // 'top' | 'bottom'
    renderControls: undefined, // fn personalizada ({ allowed, currentViewKey, setView, variant })

    // Fallback en caso de claves inexistentes
    fallbackKey: undefined,
    onMissing: undefined, // (type: 'variant'|'component', key) => ReactNode

    className: '',
    style: undefined,
  };

  constructor(props){
    super(props);

    const initialMode = this.getInitialMode(props);
    const initialView = this.getInitialViewForMode(props, initialMode);

    this.state = {
      // para modo/vista no controlados
      _mode: props.value === undefined ? initialMode : undefined,
      _viewKey: props.viewKey === undefined ? initialView : undefined,
    };

    this.setView = this.setView.bind(this);
  }

  componentDidUpdate(prevProps){
    const mode = this.getMode();
    const prevMode = this.getMode(prevProps);

    // Si el modo cambia (por el padre), resetea la vista a la default del modo
    if (mode !== prevMode){
      const nextDefaultView = this.getInitialViewForMode(this.props, mode);
      if (this.isViewControlled()){
        // si es controlled, avisa para que el padre sincronice
        if (this.props.onViewChange && nextDefaultView !== this.props.viewKey){
          this.props.onViewChange(nextDefaultView);
        }
      } else {
        this.setState({ _viewKey: nextDefaultView });
      }
    }
  }

  // -------- helpers control/uncontrol --------
  isModeControlled(){ return this.props.value !== undefined; }
  isViewControlled(){ return this.props.viewKey !== undefined; }

  getMode(p = this.props){ return this.isModeControlled() ? p.value : this.state._mode; }
  getViewKey(p = this.props){ return this.isViewControlled() ? p.viewKey : this.state._viewKey; }

  getInitialMode(p){
    if (p.value !== undefined) return p.value;
    if (p.defaultValue !== undefined) return p.defaultValue;
    // primer variant si existe
    return p.variants && p.variants.length ? p.variants[0].name : undefined;
    }

  getVariantByName(name){
    const { variants = [] } = this.props;
    return variants.find(v => v.name === name);
  }

  getInitialViewForMode(p, modeName){
    const variant = (p.variants || []).find(v => v.name === modeName);
    return variant?.defaultKey ?? p.defaultViewKey ?? p.fallbackKey;
  }

  setMode(nextMode){
    if (this.isModeControlled()){
      this.props.onChange && this.props.onChange(nextMode);
    } else {
      this.setState({ _mode: nextMode }, () => {
        this.props.onChange && this.props.onChange(nextMode);
      });
    }
  }

  setView(nextViewKey){
    if (this.isViewControlled()){
      this.props.onViewChange && this.props.onViewChange(nextViewKey);
    } else {
      this.setState({ _viewKey: nextViewKey }, () => {
        this.props.onViewChange && this.props.onViewChange(nextViewKey);
      });
    }
  }

  // -------- render --------
  renderControls(variant, currentViewKey){
    const { renderControls, showControls, controlsPosition } = this.props;
    const allowed = (variant?.allowed || [variant?.defaultKey]).filter(Boolean);

    // ¿debemos mostrar controles?
    const shouldShow = showControls === true || (showControls === 'auto' && allowed.length > 1);
    if (!shouldShow) return null;

    if (isFunction(renderControls)){
      return renderControls({ allowed, currentViewKey, setView: this.setView, variant });
    }

    // Controles por defecto: grupo de botones con labels opcionales
    const labels = variant?.labels || {};
    return (
      <div className={`emc-controls emc-controls--${controlsPosition}`}>
        <MDBBtnGroup>
          {allowed.map((key) => (
            <MDBBtn
              key={key}
              color={currentViewKey === key ? 'primary' : 'light'}
              size="sm"
              onClick={() => this.setView(key)}
            >
              {labels[key] || key}
            </MDBBtn>
          ))}
        </MDBBtnGroup>
      </div>
    );
  }

  renderCurrent(){
    const mode = this.getMode();
    const variant = this.getVariantByName(mode);

    if (!variant){
      return this.props.onMissing ? this.props.onMissing('variant', mode) : null;
    }

    const registry = this.props.registry || {};
    const viewKey = this.getViewKey();
    const keyToRender = viewKey || variant.defaultKey || this.props.fallbackKey;
    const Comp = registry[keyToRender];

    if (!Comp){
      return this.props.onMissing ? this.props.onMissing('component', keyToRender) : null;
    }

    const common = this.props.passProps || {};
    const specific = (this.props.componentProps && this.props.componentProps[keyToRender]) || {};

    return <Comp {...common} {...specific} />;
  }

  render(){
    const mode = this.getMode();
    const variant = this.getVariantByName(mode);
    const currentViewKey = this.getViewKey() || variant?.defaultKey || this.props.fallbackKey;

    const top = this.props.controlsPosition === 'top' ? this.renderControls(variant, currentViewKey) : null;
    const bottom = this.props.controlsPosition === 'bottom' ? this.renderControls(variant, currentViewKey) : null;

    return (
      <div className={`edit-mode-container ${this.props.className || ''}`} style={this.props.style}>
        {top}
        {this.renderCurrent()}
        {bottom}
      </div>
    );
  }
}
